import React, { useEffect, useRef } from 'react';

const OTP_LENGTH = 4;

export default function TripOtpInput({
  value = '',
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  hint,
}) {
  const inputRef = useRef(null);
  const code = String(value || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
  const activeIndex = Math.min(code.length, OTP_LENGTH - 1);
  const completedRef = useRef('');

  const applyCode = (raw) => {
    const next = String(raw || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    onChange?.(next);
    if (next.length === OTP_LENGTH) {
      if (next !== completedRef.current) {
        completedRef.current = next;
        onComplete?.(next);
      }
    } else {
      completedRef.current = '';
    }
  };

  useEffect(() => {
    if (code.length < OTP_LENGTH) {
      completedRef.current = '';
    }
  }, [code]);

  useEffect(() => {
    if (!autoFocus || disabled) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 160);
    return () => clearTimeout(timer);
  }, [autoFocus, disabled]);

  const focusInput = () => {
    if (!disabled) inputRef.current?.focus();
  };

  return (
    <div className="trip-otp-field">
      <div
        className="trip-otp-row"
        onClick={focusInput}
        onKeyDown={(e) => e.key === 'Enter' && focusInput()}
        role="presentation"
      >
        {Array.from({ length: OTP_LENGTH }, (_, i) => {
          const digit = code[i] || '';
          const isActive = !disabled && i === activeIndex;
          const isVerifying = disabled && code.length === OTP_LENGTH;
          return (
            <div
              key={i}
              className={`trip-otp-box${digit ? ' trip-otp-box--filled' : ''}${isActive ? ' trip-otp-box--active' : ''}${isVerifying ? ' trip-otp-box--verifying' : ''}`}
              aria-hidden="true"
            >
              {digit}
            </div>
          );
        })}
        <input
          ref={inputRef}
          className="trip-otp-input-overlay"
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="done"
          maxLength={OTP_LENGTH}
          value={code}
          disabled={disabled}
          aria-label="Trip OTP"
          onChange={(e) => applyCode(e.target.value)}
          onPaste={(e) => {
            e.preventDefault();
            applyCode(e.clipboardData.getData('text'));
          }}
        />
      </div>
      {hint ? <p className="trip-otp-hint">{hint}</p> : null}
    </div>
  );
}
